import { useState, useEffect } from "react";
import {
  Badge, Button, Card, Center, Container, Grid, Group, Loader, Modal, Select, Stack,
  Text, TextInput, Textarea, ThemeIcon, Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconBriefcase, IconChevronRight, IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { getProjects, getClients, createProject, createClient } from "../api";
import { projectStatusColor, projectStatusLabel } from "../_Shared/projectStatus";

export default function ProjectList() {
  const { t } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const form = useForm({
    initialValues: { title: "", clientId: "", roleTitle: "", roleBriefing: "" },
    validate: {
      title: (v) => (v.trim() ? null : "Title is required"),
      clientId: (v) => (v ? null : "Client is required"),
    },
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [proj, cli] = await Promise.all([getProjects(), getClients()]);
      setProjects(Array.isArray(proj) ? proj : []);
      setClients(Array.isArray(cli) ? cli : []);
    } catch (e) {
      console.error("Failed to load projects", e);
    } finally {
      setLoading(false);
    }
  }

  function getClientName(clientId) {
    return clients.find((c) => c.id === clientId)?.name ?? "";
  }

  async function handleCreate(values) {
    setSaving(true);
    try {
      const project = await createProject({
        title: values.title.trim(),
        clientId: values.clientId,
        roleTitle: values.roleTitle.trim() || undefined,
        roleBriefing: values.roleBriefing.trim() || undefined,
      });
      setCreateOpen(false);
      form.reset();
      notifications.show({ title: "Project created", message: project.title });
      setProjects((prev) => [project, ...prev]);
    } catch (e) {
      notifications.show({ color: "red", title: "Error", message: "Failed to create project" });
    } finally {
      setSaving(false);
    }
  }

  const clientOptions = clients.map((c) => ({ value: c.id, label: c.name }));

  if (loading) {
    return <Center py="xl"><Loader /></Center>;
  }

  return (
    <Container size="md" py="md">
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={3}>{t("projects")}</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          size="sm"
          onClick={() => setCreateOpen(true)}
        >
          {t("new_project")}
        </Button>
      </Group>

      {projects.length === 0 ? (
        <Card withBorder p="xl" maw={460} mx="auto" mt="md">
          <Stack gap="sm" align="center" ta="center">
            <ThemeIcon variant="light" size="xl" radius="xl"><IconBriefcase size={24} /></ThemeIcon>
            <Text fw={600}>{t("projects_empty_title")}</Text>
            <Text size="sm" c="dimmed">{t("projects_empty_help")}</Text>
            <Button
              mt="xs"
              leftSection={<IconPlus size={16} />}
              onClick={() => setCreateOpen(true)}
            >
              {t("new_project")}
            </Button>
          </Stack>
        </Card>
      ) : (
        <Grid>
          {projects.map((project) => (
            <Grid.Col key={project.id} span={{ base: 12, md: 6 }}>
              <Card
                style={{ cursor: "pointer", height: "100%", display: "flex", flexDirection: "column" }}
                onClick={() => setLocation(`/projects/${project.id}`)}
              >
                <Group justify="space-between" wrap="nowrap" align="flex-start" mb="md">
                  <Group wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                    <ThemeIcon variant="light" size="lg" style={{ flexShrink: 0 }}>
                      <IconBriefcase size={18} />
                    </ThemeIcon>
                    <div style={{ minWidth: 0 }}>
                      <Text fw={600} lineClamp={2}>{project.title}</Text>
                      <Text size="xs" c="dimmed" td="underline" lineClamp={2}
                        title={getClientName(project.clientId)}
                        style={{ cursor: "pointer", wordBreak: "break-word" }}
                        onClick={(e) => { e.stopPropagation(); setLocation(`/clients/${project.clientId}`); }}>
                        {getClientName(project.clientId)}
                      </Text>
                    </div>
                  </Group>
                  <Badge color={projectStatusColor(project.status)} style={{ flexShrink: 0 }}>{projectStatusLabel(t, project.status)}</Badge>
                </Group>

                {project.roleTitle && (
                  <Text size="sm" c="dimmed" mb="md" lineClamp={2}>{project.roleTitle}</Text>
                )}

                <Group justify="space-between" mt="auto">
                  <Text size="xs" c="dimmed">
                    {t("updated_date", { date: new Date(project.updatedAt).toLocaleDateString("de-DE") })}
                  </Text>
                  <IconChevronRight size={16} color="gray" />
                </Group>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      )}

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title={t("new_project")}>
        <form onSubmit={form.onSubmit(handleCreate)}>
          <Stack>
            <TextInput
              label={t("project_title")}
              placeholder={t("project_title_placeholder")}
              required
              {...form.getInputProps("title")}
            />
            <Select
              label={t("client")}
              placeholder={t("select_client")}
              required
              data={clientOptions}
              searchable
              {...form.getInputProps("clientId")}
            />
            <TextInput
              label={t("role_title")}
              placeholder={t("title_placeholder")}
              {...form.getInputProps("roleTitle")}
            />
            <Textarea
              label={t("role_briefing")}
              placeholder={t("role_briefing_placeholder")}
              rows={4}
              {...form.getInputProps("roleBriefing")}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreateOpen(false)}>{t("cancel")}</Button>
              <Button type="submit" loading={saving}>{t("create")}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
    </Container>
  );
}
